import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsers } from '@/hooks/useUsers';
import { useOutlets } from '@/hooks/useOutlets';
import { useAuth } from '@/lib/auth/AuthContext';
import { UserForm } from '@/components/users/UserForm';
import { api } from '@/lib/api';
import type { CreateUserRequest, UpdateUserRequest, User } from '@photobooth/types';

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const { users, isLoading, error, refetch } = useUsers();
  const { outlets } = useOutlets();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const navigate = useNavigate();

  const isCurrentUser = (userId: string) => currentUser?.id === userId;

  const nonCashierUsers = users.filter((user) => user.role !== 'cashier');

  const handleCreate = async (data: CreateUserRequest) => {
    const response = await api.users.create(data);
    if (response.success) {
      setShowCreateForm(false);
      refetch();
    } else {
      // Extract detailed validation errors if available
      const errorData = response.error as any;
      if (errorData?.details?.errors && Array.isArray(errorData.details.errors)) {
        const validationErrors = errorData.details.errors
          .map((err: any) => err.message)
          .join(', ');
        throw new Error(validationErrors);
      }
      throw new Error(response.error?.message || 'Failed to create user');
    }
  };

  const handleUpdate = async (userId: string, data: UpdateUserRequest) => {
    try {
      const response = await api.users.update(userId, data);
      if (response.success) {
        setEditingUserId(null);
        refetch();
      } else {
        throw new Error(response.error?.message || 'Failed to update user');
      }
    } catch (err) {
      throw err;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'cashier':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'cashier':
        return 'Cashier';
      default:
        return role;
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text">Admins</h1>
          <p className="text-text-muted mt-1">
            Manage admin accounts
          </p>
          <p className="text-text-muted mt-1 text-sm">
            Cashier accounts are managed on the Cashiers page.
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full md:w-auto"
        >
          + New Admin
        </button>
      </div>

      {showCreateForm && (
        <div className="mb-6 bg-surface rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-text mb-4">Create New Admin</h2>
          <UserForm
            outlets={outlets}
            mode="adminOnly"
            onSubmit={handleCreate}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      {editingUserId && (() => {
        const user = nonCashierUsers.find(u => u.userId === editingUserId) as User | undefined;
        if (!user) return null;
        return (
          <div className="mb-6 bg-surface rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-text mb-4">Edit Admin: {user.username}</h2>
            <UserForm
              user={user}
              outlets={outlets}
              mode="adminOnly"
              onSubmit={async (data) => handleUpdate(editingUserId, {
                username: data.username,
                role: data.role,
                outletId: data.outletIds?.[0] ?? data.outletId ?? undefined,
                password: data.password || undefined,
              })}
              onCancel={() => setEditingUserId(null)}
            />
          </div>
        );
      })()}

      <div className="bg-surface rounded-lg shadow overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                Username
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                Outlet
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {nonCashierUsers.map((user) => {
              const outlet = outlets.find(o => o.outletId === user.outletId);
              return (
                <tr key={user.userId}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-text">{user.username}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                    {outlet?.name || 'All'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                      }`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-3">
                      {user.role === 'cashier' ? (
                        <button
                          onClick={() => navigate(`/cashiers/${user.userId}/edit`)}
                          className="text-primary hover:text-blue-700"
                        >
                          Manage as Cashier
                        </button>
                      ) : isCurrentUser(user.userId) ? (
                        <>
                          <button
                            onClick={() => setEditingUserId(user.userId)}
                            className="text-primary hover:text-blue-700"
                          >
                            Edit
                          </button>
                          <span className="text-gray-400 text-xs">(You)</span>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
